import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';

import { SR_API_ACTION_LISTING_SUNSETUP_WITHDRAW } from 'api/actionTypes';

import {
  DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS,
  DATA_TABLE_SOURCE_SUNSETUP_WITHDRAWNS,
} from 'common/dataTableConstants';

import { LISTING_STATUS_ACTIVE, LISTING_WITHDRAWAL_REASON_LEASED } from 'common/listingConstants';

import { getHelpCardTitle } from 'common/helpCardHelpers';

import {
  hasRoleAdmin,
  hasRoleLandlord,
  hasRoleForGroupPartner,
  hasRolePartner,
} from 'common/userRoles';

import { actionDataTableReset } from 'client/helpers/dataTables';

import useBranch from 'client/hooks/useBranch';
import postWithState from 'client/actions/postWithState';

import SrCard, { SR_CARD_BULLET_COLOR_BLUE } from 'client/components/SrCard/SrCard';
import { BUTTON_THEME_GREEN_ADOBE } from 'client/components/ButtonDeprecated';
import SrBox from 'client/components/SrBox';
import SrFlex from 'client/components/SrFlex';
import SrPartnerList from 'client/components/SrPartnerList';
import SrPartnerSection from 'client/components/SrPartnerSection';
import SrSimpleMessageModal, {
  SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP,
} from 'client/components/SrSimpleMessageModal/SrSimpleMessageModal';
import SrAdminListingNotes from 'client/components/SrAdminListingNotes';
import SrPartnerPageFooter from 'client/components/SrPartnerPageFooter';
import SrPartnerListingHeader from 'client/components/SrPartnerListingHeader';
import SrHelpCardSection from 'client/components/SrHelpCardSection/SrHelpCardSection';
import SrGoingToMarketSunRiseListingCard from 'client/components/SrGoingToMarketSunRiseListingCard';
import SrListingEditWalkThroughFieldsModal from 'client/components/SrListingEditWalkThroughFieldsModal';
import SrPropertySetupDocs from 'client/components/SrPropertySetupDocs/SrPropertySetupDocs';
import SrSunSetupOwnerContact from 'client/components/SrSunSetupOwnerContact/SrSunSetupOwnerContact';
import SrPartnerListingWithdrawCard from 'client/components/SrPartnerListingWithdrawCard/SrPartnerListingWithdrawCard';
import SrPartnerListingAccessInstructions from 'client/components/SrPartnerListingAccessInstructions/SrPartnerListingAccessInstructions';
import SrPartnerMarketListingEditDueDateModal from 'client/components/SrPartnerMarketListingEditDueDateModal';
import SrPartnerListingPagePropertyConditionReport from 'client/components/SrPartnerListingPagePropertyConditionReport/SrPartnerListingPagePropertyConditionReport';
import SrPartnerListingPagePropertyConditionReportForm from 'client/components/SrPartnerListingPagePropertyConditionReport/SrPartnerListingPagePropertyConditionReportForm';
import { MARKETING_PARTNER_LISTING_STATUS_COMPLETED } from 'common/marketingPartnerListingConstants';

function getWithdrawMessage({ withdrawalReason, itemsToUninstall }) {
  if (withdrawalReason === LISTING_WITHDRAWAL_REASON_LEASED) {
    if (itemsToUninstall.length) {
      return (
        <>
          Sunroom will uninstall the home two days after the Move-In date.
          <br />
          <br />
          This listing has been moved to the Withdrawn section of the portal.
        </>
      );
    }

    return (
      <>
        This listing has been moved to the Withdrawn section of the portal.
        <br /> <br />
        Sunroom will not visit the home to remove marketing items
      </>
    );
  }

  if (itemsToUninstall.length) {
    return (
      <>
        Sunroom will uninstall the home on the date selected.
        <br />
        <br />
        This listing has been moved to the Withdrawn section of the portal.
      </>
    );
  }

  return (
    <>
      This listing has been moved to the Withdrawn section of the portal.
      <br />
      <br />
      Sunroom will not visit the home to remove marketing items
    </>
  );
}

const SrPartnerListingPageListingSunrise = ({
  listing,
  match,
  dispatch,
  marketingListing,
  helpCards,
  reloadPartnerListing,
  location,
}) => {
  const { currentUser, currentPartnerUser, partnersBySlug } = useBranch({
    appUrl: ['env', 'APP_URL'],
    currentUser: ['currentUser'],
    currentPartnerUser: ['partners', 'currentPartnerUser'],
    partnersBySlug: ['partners', 'bySlug'],
    listingLinksByListingId: ['listingLinksByListingId'],
  });

  let setupChecklistRef = null;
  useEffect(() => {
    if (location.search.includes('scrollTo=setupChecklist') && setupChecklistRef) {
      setupChecklistRef.scrollIntoView({
        behavior: 'smooth',
      });
    }
  });

  const [
    shouldShowListingEditWalkThroughFieldsModal,
    setShouldShowListingEditWalkThroughFieldsModal,
  ] = useState(null);

  const [editDueDateModalProps, setEditDueDateModalProps] = useState(null);

  const [listingWithdrawModalProps, setListingWithdrawModalProps] = useState(null);

  const currentPartner = partnersBySlug[match.params.partnerSlug];
  const isLandlord = hasRoleLandlord(currentUser.roles);
  const isAdmin = hasRoleAdmin(currentUser.roles);
  const isPartner = hasRolePartner(currentUser.roles);
  const showPropertyConditionReport =
    marketingListing?.propertyConditionOverall != null && (isPartner || isAdmin);
  const showPropertyConditionReportForm = marketingListing && isAdmin;
  const showPropertyConditionReportSection =
    showPropertyConditionReport || showPropertyConditionReportForm;
  const isCompleted = marketingListing?.status === MARKETING_PARTNER_LISTING_STATUS_COMPLETED;

  const onWithdrawn = (payload) => {
    dispatch(postWithState, {
      action: SR_API_ACTION_LISTING_SUNSETUP_WITHDRAW,
      payload: payload,
      onSuccess: () => {
        setListingWithdrawModalProps(payload);

        reloadPartnerListing();

        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_SUNSETUP_COMPLETED_SETUPS,
        });

        dispatch(actionDataTableReset, {
          dataSource: DATA_TABLE_SOURCE_SUNSETUP_WITHDRAWNS,
        });
      },
    });
  };

  return (
    <div>
      <SrPartnerListingHeader
        listing={listing}
        marketingPartnerListing={marketingListing}
        currentUser={currentUser}
        currentPartnerUser={currentPartnerUser}
        currentPartner={currentPartner}
        onOpenListingLinksClick={() => {}}
        onOpenShareListingClick={() => {}}
        onOpenSupportLinksClick={() => {}}
        partnerSlug={match.params.partnerSlug}
        isLandlord={isLandlord}
        dispatch={dispatch}
        onLowerRentPrice={reloadPartnerListing}
      />
      <SrBox margin="55px auto 0" padding="0 10px" maxWidth="1180px">
        {isAdmin && (
          <SrBox marginTop="50px">
            <SrCard header="Notes (Admin-Only)" color={SR_CARD_BULLET_COLOR_BLUE} maxWidth="none">
              <SrAdminListingNotes
                listingId={listing.id}
                dispatch={dispatch}
                currentUser={currentUser}
              />
            </SrCard>
          </SrBox>
        )}
        <SrPartnerList twoColumns>
          <SrFlex direction="col">
            {(!isCompleted || isAdmin) && (
              <SrPartnerSection title={isCompleted ? 'Past Setup (admin only)' : 'Setup Checklist'}>
                <div
                  style={{
                    position: 'relative',
                    top: '-150px',
                  }}
                  ref={(node) => {
                    setupChecklistRef = node;
                  }}
                />
                <SrGoingToMarketSunRiseListingCard
                  isAdmin={isAdmin}
                  marketingListing={marketingListing}
                  onEditWalkThroughFields={() =>
                    setShouldShowListingEditWalkThroughFieldsModal(true)
                  }
                  onEditMarketingPartnerListingDueDate={() =>
                    setEditDueDateModalProps(marketingListing)
                  }
                  isHighlighted={location.search.includes('scrollTo=setupChecklist')}
                  reloadData={() => reloadPartnerListing()}
                />
              </SrPartnerSection>
            )}
            {showPropertyConditionReportSection && (
              <SrPartnerSection title="Property Condition Report">
                {showPropertyConditionReport && (
                  <SrPartnerListingPagePropertyConditionReport
                    showEdit={false}
                    marketingListing={marketingListing}
                  />
                )}
                {showPropertyConditionReportForm && (
                  <SrPartnerListingPagePropertyConditionReportForm
                    marketingListing={marketingListing}
                    reloadPartnerListing={reloadPartnerListing}
                  />
                )}
              </SrPartnerSection>
            )}
          </SrFlex>
          <SrPartnerSection title="Property Setup Docs">
            <SrPropertySetupDocs listing={listing} match={match} />
          </SrPartnerSection>
        </SrPartnerList>
        <SrPartnerList twoColumns>
          <SrPartnerSection title="Owner Contact">
            <SrSunSetupOwnerContact listing={listing} />
          </SrPartnerSection>
          <SrPartnerSection title="Property Access">
            <SrPartnerListingAccessInstructions
              listing={listing}
              marketingPartnerListing={marketingListing}
              currentUser={currentUser}
            />
          </SrPartnerSection>
        </SrPartnerList>
        {hasRoleForGroupPartner(currentUser.roles) && listing.status === LISTING_STATUS_ACTIVE && (
          <SrPartnerListingWithdrawCard
            listing={listing}
            partner={currentPartner}
            onWithdrawn={onWithdrawn}
          />
        )}
        {helpCards && helpCards.length > 0 && (
          <SrBox marginTop="80px">
            <SrHelpCardSection
              helpCards={helpCards}
              title={getHelpCardTitle({ partner: currentPartner })}
            />
          </SrBox>
        )}
        <SrPartnerPageFooter />
      </SrBox>
      {shouldShowListingEditWalkThroughFieldsModal && (
        <SrListingEditWalkThroughFieldsModal
          marketingPartnerListing={marketingListing}
          listingId={marketingListing.listingId}
          dispatch={dispatch}
          onClose={() => setShouldShowListingEditWalkThroughFieldsModal(false)}
          reloadDataTableMarketingPartnerListing={() => reloadPartnerListing()}
        />
      )}
      {editDueDateModalProps && (
        <SrPartnerMarketListingEditDueDateModal
          marketingPartnerListing={editDueDateModalProps}
          dispatch={dispatch}
          reloadDataTableMarketingPartnerListing={() => reloadPartnerListing()}
          onClose={() => setEditDueDateModalProps(null)}
        />
      )}
      {listingWithdrawModalProps && (
        <SrSimpleMessageModal
          theme={SR_SIMPLE_MESSAGE_MODAL_THEME_SLIDE_UP}
          iconUrl="/images/checkmarkSuccessGreen.svg"
          headline="Withdraw ordered!"
          message={getWithdrawMessage(listingWithdrawModalProps)}
          buttonText="Ok, thanks"
          buttonTheme={BUTTON_THEME_GREEN_ADOBE}
          maxWidth={375}
          onClose={() => {
            setListingWithdrawModalProps(null);
          }}
        />
      )}
    </div>
  );
};

SrPartnerListingPageListingSunrise.propTypes = {
  match: PropTypes.object,
  listing: PropTypes.object,
  marketingListing: PropTypes.object,
  dispatch: PropTypes.func.isRequired,
  reloadPartnerListing: PropTypes.func.isRequired,
  location: PropTypes.object.isRequired,
};

SrPartnerListingPageListingSunrise.displayName = 'SrPartnerListingPageListingSunrise';

export default SrPartnerListingPageListingSunrise;
